import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ArrowLeft, Search, Package, RefreshCw, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ERPCatalogSync() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [purityFilter, setPurityFilter] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [searchInput, setSearchInput] = useState('');

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get(`${BACKEND_URL}/api/erp/catalog/stats`);
      setStats(res.data);
    } catch (e) {
      // non-critical
    }
  }, []);

  const fetchItems = useCallback(async (pg = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: pg, limit: 24 });
      if (search) params.append('search', search);
      if (categoryFilter && categoryFilter !== 'all') params.append('category', categoryFilter);
      if (purityFilter && purityFilter !== 'all') params.append('purity', purityFilter);
      const res = await api.get(`${BACKEND_URL}/api/erp/catalog?${params}`);
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 1);
      setPage(pg);
    } catch (e) {
      toast.error('Failed to load catalog');
    } finally {
      setLoading(false);
    }
  }, [search, categoryFilter, purityFilter]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchItems(1);
  }, [fetchItems]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
  };

  const clearFilters = () => {
    setSearch('');
    setSearchInput('');
    setCategoryFilter('');
    setPurityFilter('');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
        <button
          onClick={() => navigate(-1)}
          className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
          data-testid="erp-catalog-back-btn"
        >
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </button>
        <div className="flex-1">
          <h1 className="text-base font-semibold text-gray-900">ERP Catalog</h1>
          <p className="text-xs text-gray-500">
            {total > 0 ? `${total} items synced` : 'No items synced yet'}
            {stats?.last_synced_at && (
              <span className="ml-2 text-gray-400">
                · Last sync: {new Date(stats.last_synced_at).toLocaleString('en-IN')}
              </span>
            )}
          </p>
        </div>
        <button
          onClick={() => { fetchStats(); fetchItems(1); }}
          className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
          data-testid="erp-catalog-refresh-btn"
          title="Refresh"
        >
          <RefreshCw className="w-4 h-4 text-gray-500" />
        </button>
      </div>

      {/* Stats bar */}
      {stats && stats.total_items > 0 && (
        <div className="bg-white border-b border-gray-100 px-4 py-2 flex gap-4 overflow-x-auto text-xs text-gray-500">
          <span className="font-medium text-gray-700">{stats.total_items} items</span>
          {stats.categories?.slice(0, 5).map(c => (
            <span
              key={c}
              className="cursor-pointer hover:text-gray-900 transition-colors whitespace-nowrap"
              onClick={() => setCategoryFilter(c)}
            >
              {c}
            </span>
          ))}
          {stats.purities?.slice(0, 4).map(p => (
            <span
              key={p}
              className="cursor-pointer text-amber-600 hover:text-amber-800 transition-colors whitespace-nowrap"
              onClick={() => setPurityFilter(p)}
            >
              {p}
            </span>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="px-4 py-3 bg-white border-b border-gray-100 flex gap-2 flex-wrap items-center">
        <form onSubmit={handleSearch} className="flex gap-2 flex-1 min-w-[200px]">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search by SKU, name, category..."
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              className="pl-9 h-9 text-sm"
              data-testid="erp-catalog-search-input"
            />
          </div>
          <Button type="submit" size="sm" className="h-9" data-testid="erp-catalog-search-btn">
            Search
          </Button>
        </form>

        {stats?.categories?.length > 0 && (
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-36 h-9 text-sm" data-testid="erp-catalog-category-filter">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {stats.categories.map(c => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {stats?.purities?.length > 0 && (
          <Select value={purityFilter} onValueChange={setPurityFilter}>
            <SelectTrigger className="w-28 h-9 text-sm" data-testid="erp-catalog-purity-filter">
              <SelectValue placeholder="Purity" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Purity</SelectItem>
              {stats.purities.map(p => (
                <SelectItem key={p} value={p}>{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {(search || (categoryFilter && categoryFilter !== 'all') || (purityFilter && purityFilter !== 'all')) && (
          <Button variant="ghost" size="sm" onClick={clearFilters} className="h-9 text-xs text-gray-500">
            Clear
          </Button>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <RefreshCw className="w-8 h-8 text-gray-300 animate-spin mx-auto mb-3" />
              <p className="text-sm text-gray-400">Loading catalog...</p>
            </div>
          </div>
        ) : items.length === 0 ? (
          <EmptyState search={search} total={total} />
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
              {items.map(item => (
                <CatalogCard
                  key={item.sku}
                  item={item}
                  onClick={() => setSelectedItem(item)}
                />
              ))}
            </div>

            {/* Pagination */}
            {pages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-6">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchItems(page - 1)}
                  disabled={page === 1}
                  data-testid="erp-catalog-prev-page"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm text-gray-600">
                  Page {page} of {pages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchItems(page + 1)}
                  disabled={page === pages}
                  data-testid="erp-catalog-next-page"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Item Detail Dialog */}
      {selectedItem && (
        <ItemDetailDialog
          item={selectedItem}
          onClose={() => setSelectedItem(null)}
        />
      )}
    </div>
  );
}

function CatalogCard({ item, onClick }) {
  return (
    <div
      className="bg-white rounded-xl border border-gray-100 overflow-hidden cursor-pointer hover:shadow-md hover:border-gray-300 transition-all duration-200 group"
      onClick={onClick}
      data-testid={`erp-catalog-item-${item.sku}`}
    >
      {/* Image */}
      <div className="aspect-square bg-gray-50 overflow-hidden relative">
        {item.image_url ? (
          <img
            src={item.image_url}
            alt={item.item_name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
          />
        ) : null}
        <div
          className={`absolute inset-0 flex items-center justify-center bg-gray-100 ${item.image_url ? 'hidden' : 'flex'}`}
        >
          <Package className="w-10 h-10 text-gray-300" />
        </div>
        {item.purity && (
          <div className="absolute top-1.5 right-1.5">
            <Badge variant="secondary" className="text-[10px] px-1.5 py-0 bg-amber-50 text-amber-700 border-amber-200">
              {item.purity}
            </Badge>
          </div>
        )}
      </div>

      {/* Info */}
      <div className="p-2">
        <p className="text-xs font-medium text-gray-900 line-clamp-2 leading-tight">{item.item_name}</p>
        <p className="text-[10px] text-gray-400 mt-0.5 font-mono">{item.sku}</p>
        {item.gross_weight && (
          <p className="text-[10px] text-gray-500 mt-0.5">{item.gross_weight}g gross</p>
        )}
        {item.stones?.length > 0 && (
          <p className="text-[10px] text-blue-500 mt-0.5">
            {item.stones.length} stone{item.stones.length > 1 ? 's' : ''}
          </p>
        )}
      </div>
    </div>
  );
}

function ItemDetailDialog({ item, onClose }) {
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="erp-catalog-item-detail">
        <DialogHeader>
          <DialogTitle className="text-base">{item.item_name}</DialogTitle>
          <p className="text-xs text-gray-400 font-mono">{item.sku}</p>
        </DialogHeader>

        <div className="space-y-4">
          {/* Image */}
          {item.image_url && (
            <div className="rounded-lg overflow-hidden bg-gray-50 aspect-video">
              <img
                src={item.image_url}
                alt={item.item_name}
                className="w-full h-full object-contain"
              />
            </div>
          )}

          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            {item.category && <Field label="Category" value={item.category} />}
            {item.subcategory && <Field label="Sub-category" value={item.subcategory} />}
            {item.purity && <Field label="Purity" value={item.purity} />}
            {item.pieces != null && <Field label="Pieces" value={item.pieces} />}
            {item.gross_weight != null && <Field label="Gross Wt." value={`${item.gross_weight}g`} />}
            {item.net_weight != null && <Field label="Net Wt." value={`${item.net_weight}g`} />}
            {item.metal_weight != null && <Field label="Metal Wt." value={`${item.metal_weight}g`} />}
          </div>

          {/* Stones */}
          {item.stones?.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                Stones ({item.stones.length})
              </p>
              <div className="space-y-2">
                {item.stones.map((stone, i) => (
                  <div key={i} className="bg-gray-50 rounded-lg p-3 text-xs space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{stone.stone_type}</span>
                      {stone.stone_name && <span className="text-gray-500">· {stone.stone_name}</span>}
                      {stone.shape && <span className="text-gray-500">· {stone.shape}</span>}
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-gray-600">
                      {stone.pieces != null && <span>{stone.pieces} pcs</span>}
                      {stone.total_weight != null && <span>{stone.total_weight} ct total</span>}
                      {stone.weight_per_piece != null && <span>{stone.weight_per_piece} ct each</span>}
                      {stone.size && <span>Size: {stone.size}</span>}
                      {stone.grade && <span>{stone.grade}</span>}
                      {stone.color && <span>{stone.color}</span>}
                      {stone.cut && <span>{stone.cut}</span>}
                      {stone.category && (
                        <Badge variant="outline" className="text-[10px] px-1 py-0">
                          {stone.category}
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tags */}
          {item.tags?.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {item.tags.map(tag => (
                <Badge key={tag} variant="secondary" className="text-xs">{tag}</Badge>
              ))}
            </div>
          )}

          {/* Custom fields */}
          {item.custom_fields && Object.keys(item.custom_fields).length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">Custom Fields</p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                {Object.entries(item.custom_fields).map(([k, v]) => (
                  <Field key={k} label={k} value={String(v)} />
                ))}
              </div>
            </div>
          )}

          {/* Image URL link */}
          {item.image_url && (
            <a
              href={item.image_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-xs text-blue-500 hover:underline"
            >
              <ExternalLink className="w-3 h-3" /> View original image
            </a>
          )}

          <p className="text-[10px] text-gray-400">
            Last synced: {item.synced_at ? new Date(item.synced_at).toLocaleString('en-IN') : 'Unknown'}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <p className="text-gray-400 text-[10px] uppercase tracking-wide">{label}</p>
      <p className="font-medium text-gray-800">{value}</p>
    </div>
  );
}

function EmptyState({ search, total }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <Package className="w-14 h-14 text-gray-200 mb-4" />
      {total === 0 && !search ? (
        <>
          <p className="text-base font-medium text-gray-700">No items synced yet</p>
          <p className="text-sm text-gray-400 mt-1 max-w-xs">
            Once your ERP engineer connects the API, catalog items will appear here in real-time.
          </p>
        </>
      ) : (
        <>
          <p className="text-base font-medium text-gray-700">No items found</p>
          <p className="text-sm text-gray-400 mt-1">Try a different search or clear the filters.</p>
        </>
      )}
    </div>
  );
}
